import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../api/client.js';
import {
  EVENT_TYPES, POKEMON_ROLES, formatRange, eventStatus,
  relativeLabel, eventColor, typeColor,
} from '../lib/format.js';
import { EmpfehlungsBadge, EmpfehlungsZusammenfassung } from '../components/Empfehlung.jsx';
import PvpBadge from '../components/PvpBadge.jsx';
import { useAuth } from '../auth/AuthContext.jsx';
import EventTeilnahme from '../components/EventTeilnahme.jsx';

export default function EventDetailPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    setEvent(null);
    setError(null);
    api.events.get(slug)
      .then((data) => active && setEvent(data.event))
      .catch((err) => active && setError(err.message));
    return () => { active = false; };
  }, [slug, user]);

  if (error) {
    return (
      <>
        <div className="error">{error}</div>
        <p style={{ marginTop: 16 }}><Link to="/" className="nav__link">← Zurück zur Übersicht</Link></p>
      </>
    );
  }

  if (!event) return <div className="skeleton" style={{ height: 260 }} />;

  const accent = eventColor(event);
  const status = eventStatus(event);
  const grouped = groupByRole(event.pokemon ?? []);

  return (
    <>
      <p style={{ marginBottom: 16 }}><Link to="/" className="nav__link">← Alle Events</Link></p>

      <header className="detail__head" style={{ '--card-accent': accent }}>
        <div className="inline" style={{ marginBottom: 12 }}>
          <span className="badge" style={{ '--badge-color': accent }}>
            {EVENT_TYPES[event.eventType] ?? EVENT_TYPES.other}
          </span>
          {status === 'live'
            ? <span className="badge badge--live">Live</span>
            : <span className="badge badge--soft">{relativeLabel(event)}</span>}
          <EmpfehlungsZusammenfassung summary={event.personalSummary} />
        </div>

        <h1>{event.title}</h1>
        <p className="hero__sub" style={{ marginTop: 10 }}>{formatRange(event.startsAt, event.endsAt)}</p>
        {event.summary && <p className="hero__sub">{event.summary}</p>}
      </header>

      {user && <EventTeilnahme eventSlug={event.slug} eventTitel={event.title} />}

      {event.description && (
        <section className="section">
          <h2 className="section__title">Worum es geht</h2>
          <div className="panel panel--prose">
            {/* Echte Absaetze statt pre-line: nur so laesst sich der Abstand
                zwischen ihnen steuern, und Leerzeilen aus dem Admin erzeugen
                keine unterschiedlich grossen Luecken. */}
            <div className="prose">
              {event.description.split(/\n\s*\n/).map((absatz, i) => (
                <p key={i}>{absatz.trim()}</p>
              ))}
            </div>
          </div>
        </section>
      )}

      {event.bonuses?.length > 0 && (
        <section className="section">
          <h2 className="section__title">Boni</h2>
          <ul className="bonus-list">
            {event.bonuses.map((bonus) => <li key={bonus}>{bonus}</li>)}
          </ul>
        </section>
      )}

      {Object.entries(grouped).map(([role, mons]) => (
        <section className="section" key={role}>
          <h2 className="section__title">{POKEMON_ROLES[role] ?? role}</h2>
          <div className="mon-grid">
            {mons.map((mon) => (
              <Link to={`/pokemon/${mon.slug}`} className="mon" key={`${mon.id}-${mon.role}`}>
                <div className="spread">
                  <span className="mon__name">{mon.name}</span>
                  <span className="mon__dex">#{String(mon.dexNo).padStart(3, '0')}</span>
                </div>
                {mon.form && <span className="badge badge--soft">{mon.form}</span>}
                <div className="inline">
                  {mon.types.map((t) => (
                    <span className="type" key={t} style={{ '--type-color': typeColor(t) }}>{t}</span>
                  ))}
                </div>
                {mon.shinyBoosted && <span className="badge badge--soft">Shiny-Chance erhöht</span>}
                {mon.pvp && <div className="inline"><PvpBadge pvp={mon.pvp} /></div>}
                {mon.personal && (
                  <div className="inline"><EmpfehlungsBadge personal={mon.personal} /></div>
                )}
                {mon.note && <p className="card__summary">{mon.note}</p>}
              </Link>
            ))}
          </div>
        </section>
      ))}

      {/* Phase 2: hier kommt der Lohnt-sich-Score auf Basis der Raid-Bewertung rein. */}

      {event.source?.name && (
        <p className="footer" style={{ borderTop: 'none', marginTop: 24 }}>
          Quelle: {event.source.url
            ? <a href={event.source.url} target="_blank" rel="noreferrer noopener">{event.source.name}</a>
            : event.source.name}
        </p>
      )}
    </>
  );
}

function groupByRole(pokemon) {
  return pokemon.reduce((acc, mon) => {
    (acc[mon.role] ??= []).push(mon);
    return acc;
  }, {});
}
